(function(){
/*
 * Koda site search client.
 *
 * Loads the index built by koda-search, answers queries in memory, and
 * renders results grouped by page with the matching section's heading path
 * and a preview of the matching block as the page renders it.
 *
 * The tokenizer here mirrors crates/search/src/tokenize.rs and is checked
 * against it by a test; keep the two in sync.
 *
 * Page contract: an element with `data-search-input` (an <input>) and one
 * with `data-search-results`. Index and stores are fetched relative to
 * this script's own URL.
 */

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

/** Strip diacritics (keeping kana voicing marks) and normalize. */
function fold(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/\p{M}/gu, (m) => (m === "゙" || m === "゚" ? m : ""))
    .normalize("NFC");
}

function isCjk(cp: number): boolean {
  return (
    (cp >= 0x3040 && cp <= 0x30ff) ||
    (cp >= 0x3400 && cp <= 0x4dbf) ||
    (cp >= 0x4e00 && cp <= 0x9fff) ||
    (cp >= 0xac00 && cp <= 0xd7af) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0x20000 && cp <= 0x2ffff)
  );
}

const WORD_CHAR = /[\p{Alphabetic}\p{N}_']/u;
const UPPER = /\p{Uppercase}/u;
const LOWER = /\p{Lowercase}/u;
const NUMERIC = /\p{N}/u;

/** Split an identifier at underscores and camelCase boundaries. */
function splitIdentifier(word: string): string[] {
  const parts: string[] = [];
  for (const segment of word.split("_")) {
    if (segment === "") continue;
    const chars = Array.from(segment);
    if (NUMERIC.test(chars[0])) {
      parts.push(segment);
      continue;
    }
    let start = 0;
    for (let i = 1; i < chars.length; i++) {
      const c = chars[i];
      const prev = chars[i - 1];
      const nextLower = i + 1 < chars.length && LOWER.test(chars[i + 1]);
      const boundary =
        UPPER.test(c) &&
        (LOWER.test(prev) || NUMERIC.test(prev) || (UPPER.test(prev) && nextLower));
      if (boundary) {
        parts.push(chars.slice(start, i).join(""));
        start = i;
      }
    }
    parts.push(chars.slice(start).join(""));
  }
  return parts;
}

/** A run of the input that produced index terms, with its span in the input. */
interface Run {
  start: number;
  end: number;
  /** Stemmed terms. */
  terms: string[];
  /** Folded, unstemmed words, parallel to `terms`. */
  forms: string[];
}

const foldCache = new Map<string, string>();

function foldChar(ch: string): string {
  let f = foldCache.get(ch);
  if (f === undefined) {
    f = fold(ch);
    foldCache.set(ch, f);
  }
  return f;
}

/** Tokenize `text`, keeping the input span of each word or CJK run. */
function scan(text: string): Run[] {
  const runs: Run[] = [];
  let word = "";
  let wordStart = -1;
  let cjk: string[] = [];
  let cjkStart = -1;

  const flushWord = (end: number) => {
    if (word === "") return;
    const trimmed = word.replace(/^'+|'+$/g, "");
    if (trimmed !== "") {
      const parts = splitIdentifier(trimmed);
      const forms: string[] = [];
      if (parts.length > 1) forms.push(trimmed.toLowerCase());
      for (const part of parts) forms.push(part.toLowerCase());
      runs.push({ start: wordStart, end, terms: forms.map(stem), forms });
    }
    word = "";
    wordStart = -1;
  };
  const flushCjk = (end: number) => {
    if (cjk.length === 0) return;
    const terms: string[] = [];
    if (cjk.length === 1) terms.push(cjk[0]);
    else for (let i = 0; i + 1 < cjk.length; i++) terms.push(cjk[i] + cjk[i + 1]);
    runs.push({ start: cjkStart, end, terms, forms: terms });
    cjk = [];
    cjkStart = -1;
  };

  let offset = 0;
  for (const ch of text) {
    const next = offset + ch.length;
    for (const c of foldChar(ch)) {
      const cp = c.codePointAt(0)!;
      if (isCjk(cp)) {
        flushWord(offset);
        if (cjkStart < 0) cjkStart = offset;
        cjk.push(c);
      } else if (WORD_CHAR.test(c)) {
        flushCjk(offset);
        if (wordStart < 0) wordStart = offset;
        word += c;
      } else {
        flushWord(offset);
        flushCjk(offset);
      }
    }
    offset = next;
  }
  flushWord(offset);
  flushCjk(offset);
  return runs;
}

function tokenize(text: string): string[] {
  const out: string[] = [];
  for (const run of scan(text)) out.push(...run.terms);
  return out;
}

function tokenizeForms(text: string): string[] {
  const out: string[] = [];
  for (const run of scan(text)) out.push(...run.forms);
  return out;
}

// ---------------------------------------------------------------------------
// Snowball English stemmer (Snowball 3.0), matching pagefind_stem.
// ---------------------------------------------------------------------------

const EXCEPTIONS = new Map<string, string>([
  ["skis", "ski"],
  ["skies", "sky"],
  ["idly", "idl"],
  ["gently", "gentl"],
  ["ugly", "ugli"],
  ["early", "earli"],
  ["only", "onli"],
  ["singly", "singl"],
  ["sky", "sky"],
  ["news", "news"],
  ["howe", "howe"],
  ["atlas", "atlas"],
  ["cosmos", "cosmos"],
  ["bias", "bias"],
  ["andes", "andes"],
]);
const R1_PREFIXES = ["arsen", "commun", "emerg", "gener", "inter", "later", "organ", "past", "univers"];
const STEP2: [string, string | null][] = [
  ["ization", "ize"],
  ["ational", "ate"],
  ["fulness", "ful"],
  ["ousness", "ous"],
  ["iveness", "ive"],
  ["tional", "tion"],
  ["biliti", "ble"],
  ["lessli", "less"],
  ["entli", "ent"],
  ["ation", "ate"],
  ["alism", "al"],
  ["aliti", "al"],
  ["ousli", "ous"],
  ["iviti", "ive"],
  ["fulli", "ful"],
  ["ogist", "og"],
  ["enci", "ence"],
  ["anci", "ance"],
  ["abli", "able"],
  ["izer", "ize"],
  ["ator", "ate"],
  ["alli", "al"],
  ["ogi", "og"],
  ["bli", "ble"],
  ["li", null],
];
const STEP3: [string, string | null][] = [
  ["ational", "ate"],
  ["tional", "tion"],
  ["alize", "al"],
  ["icate", "ic"],
  ["iciti", "ic"],
  ["ative", null],
  ["ical", "ic"],
  ["ness", null],
  ["ful", null],
];
const STEP4 = [
  "ement", "ance", "ence", "able", "ible", "ment", "ant", "ent", "ism", "ate",
  "iti", "ous", "ive", "ize", "ion", "al", "er", "ic",
];

function isVowel(c: string | undefined): boolean {
  return c !== undefined && "aeiouy".includes(c);
}

/** Start of the region after the first non-vowel that follows a vowel. */
function regionAfter(w: string, from: number): number {
  let i = from;
  while (i < w.length && !isVowel(w[i])) i++;
  while (i < w.length && isVowel(w[i])) i++;
  return i < w.length ? i + 1 : w.length;
}

/** Whether `w` ends in a short syllable. */
function shortSyllable(w: string): boolean {
  const n = w.length;
  if (n >= 3 && !isVowel(w[n - 1]) && !"wxY".includes(w[n - 1]) && isVowel(w[n - 2]) && !isVowel(w[n - 3])) {
    return true;
  }
  if (n === 2 && !isVowel(w[1]) && isVowel(w[0])) return true;
  return w.endsWith("past");
}

function stem(word: string): string {
  const exception = EXCEPTIONS.get(word);
  if (exception !== undefined) return exception;
  if (word.length < 3) return word;

  let w = word;
  let yFound = false;

  // Prelude: drop a leading apostrophe, mark consonant y as Y.
  if (w[0] === "'") w = w.slice(1);
  const chars = Array.from(w);
  if (chars[0] === "y") {
    chars[0] = "Y";
    yFound = true;
  }
  for (let i = 1; i < chars.length; i++) {
    if (chars[i] === "y" && isVowel(chars[i - 1])) {
      chars[i] = "Y";
      yFound = true;
    }
  }
  w = chars.join("");

  // Regions.
  const prefix = R1_PREFIXES.find((p) => w.startsWith(p));
  const p1 = prefix !== undefined ? prefix.length : regionAfter(w, 0);
  const p2 = regionAfter(w, p1);
  const inR1 = (suffixLen: number) => w.length - suffixLen >= p1;
  const inR2 = (suffixLen: number) => w.length - suffixLen >= p2;

  // Step 0: apostrophes.
  if (w.endsWith("'s'")) w = w.slice(0, -3);
  else if (w.endsWith("'s")) w = w.slice(0, -2);
  else if (w.endsWith("'")) w = w.slice(0, -1);

  // Step 1a.
  if (w.endsWith("sses")) {
    w = w.slice(0, -2);
  } else if (w.endsWith("ied") || w.endsWith("ies")) {
    w = w.slice(0, -3) + (w.length - 3 >= 2 ? "i" : "ie");
  } else if (w.endsWith("ss") || w.endsWith("us")) {
    // unchanged
  } else if (w.endsWith("s")) {
    let hasVowel = false;
    for (let i = 0; i < w.length - 2; i++) {
      if (isVowel(w[i])) {
        hasVowel = true;
        break;
      }
    }
    if (hasVowel) w = w.slice(0, -1);
  }

  // Step 1b.
  const step1b = (suffix: string): boolean => {
    if (!w.endsWith(suffix)) return false;
    const stemPart = w.slice(0, -suffix.length);
    if (suffix === "eed" || suffix === "eedly") {
      if (inR1(suffix.length) && !["succ", "proc", "exc"].includes(stemPart)) {
        w = stemPart + "ee";
      }
      return true;
    }
    if (suffix === "ing" || suffix === "ingly") {
      if (stemPart.length === 2 && stemPart[1] === "y" && !isVowel(stemPart[0])) {
        w = stemPart[0] + "ie";
        return true;
      }
      if (["even", "cann", "inn", "earr", "herr", "out"].includes(stemPart)) return true;
    }
    if (!Array.from(stemPart).some(isVowel)) return true;
    w = stemPart;
    if (w.endsWith("at") || w.endsWith("bl") || w.endsWith("iz")) {
      w += "e";
    } else if (/(bb|dd|ff|gg|mm|nn|pp|rr|tt)$/.test(w)) {
      if (!(w.length === 3 && "aeo".includes(w[0]))) w = w.slice(0, -1);
    } else if (w.length === p1 && shortSyllable(w)) {
      w += "e";
    }
    return true;
  };
  for (const suffix of ["eedly", "ingly", "edly", "eed", "ing", "ed"]) {
    if (step1b(suffix)) break;
  }

  // Step 1c.
  if ((w.endsWith("y") || w.endsWith("Y")) && w.length >= 3 && !isVowel(w[w.length - 2])) {
    w = w.slice(0, -1) + "i";
  }

  // Step 2.
  for (const [suffix, replacement] of STEP2) {
    if (!w.endsWith(suffix)) continue;
    if (inR1(suffix.length)) {
      const before = w[w.length - suffix.length - 1];
      if (suffix === "ogi") {
        if (before === "l") w = w.slice(0, -suffix.length) + "og";
      } else if (suffix === "li") {
        if (before !== undefined && "cdeghkmnrt".includes(before)) w = w.slice(0, -2);
      } else {
        w = w.slice(0, -suffix.length) + replacement;
      }
    }
    break;
  }

  // Step 3.
  for (const [suffix, replacement] of STEP3) {
    if (!w.endsWith(suffix)) continue;
    if (inR1(suffix.length)) {
      if (suffix === "ative") {
        if (inR2(suffix.length)) w = w.slice(0, -suffix.length);
      } else {
        w = w.slice(0, -suffix.length) + (replacement ?? "");
      }
    }
    break;
  }

  // Step 4.
  for (const suffix of STEP4) {
    if (!w.endsWith(suffix)) continue;
    if (inR2(suffix.length)) {
      if (suffix === "ion") {
        const before = w[w.length - 4];
        if (before === "s" || before === "t") w = w.slice(0, -3);
      } else {
        w = w.slice(0, -suffix.length);
      }
    }
    break;
  }

  // Step 5.
  if (w.endsWith("e")) {
    if (inR2(1) || (inR1(1) && !shortSyllable(w.slice(0, -1)))) w = w.slice(0, -1);
  } else if (w.endsWith("l")) {
    if (inR2(1) && w[w.length - 2] === "l") w = w.slice(0, -1);
  }

  return yFound ? w.replace(/Y/g, "y") : w;
}

// ---------------------------------------------------------------------------
// Index and scoring
// ---------------------------------------------------------------------------

interface SectionEntry {
  id: string;
  headings: string[];
  len: [number, number, number, number];
}
interface DocEntry {
  url: string;
  title: string;
  description: string;
  store: string;
  len: [number, number];
  sections: SectionEntry[];
}
/** doc, section, block, field, count */
type Posting = [number, number, number, number, number];
interface Index {
  version: number;
  docs: DocEntry[];
  terms: string[];
  /** Unstemmed forms per term, excluding the term itself. */
  forms: string[][];
  /** Byte offsets of each term's postings in the postings file, plus the end. */
  offsets: number[];
}

/**
 * Decode one term's postings: five unsigned LEB128 varints per posting,
 * with the document id delta-coded and section and block shifted by one.
 */
function decodePostings(bytes: Uint8Array, start: number, end: number): Posting[] {
  const out: Posting[] = [];
  let pos = start;
  let doc = 0;
  const varint = () => {
    let value = 0;
    let shift = 0;
    for (;;) {
      const byte = bytes[pos++];
      value += (byte & 0x7f) * 2 ** shift;
      if ((byte & 0x80) === 0) return value;
      shift += 7;
    }
  };
  while (pos < end) {
    doc += varint();
    const section = varint() - 1;
    const block = varint() - 1;
    const field = varint();
    const count = varint();
    out.push([doc, section, block, field, count]);
  }
  return out;
}

function decodePostingsForTest(bytes: number[], start: number, end: number): Posting[] {
  return decodePostings(Uint8Array.from(bytes), start, end);
}
interface StoreBlock {
  kind: "p" | "c" | "a";
  lang?: string;
  html: string;
}
interface Store {
  styles: string[];
  blocks: StoreBlock[][];
}

const FIELD_WEIGHT = [8, 3, 5, 1, 0.7, 0.6]; // title, description, heading, prose, code, aside
const BM25_K1 = 1.2;
const BM25_B = 0.75;
const PREFIX_WEIGHT = 0.6;
const MAX_RESULTS = 20;
const MAX_SECTIONS = 3;
const CODE_CONTEXT_LINES = 3;

interface SectionHit {
  section: number;
  score: number;
  block: number;
}
interface Result {
  doc: number;
  score: number;
  sections: SectionHit[];
}
interface Search {
  results: Result[];
  /** Every index term any query token matched; drives highlighting. */
  matched: Set<string>;
}

/** A query token: its stem and the word as typed, folded. */
interface QueryToken {
  term: string;
  form: string;
}

class SearchIndex {
  private avgLen: number[];
  /** Every surface form with its term index, sorted by form. */
  private forms: [string, number][];
  /** Postings decoded so far, by term index. */
  private decoded = new Map<number, Posting[]>();
  private docFreq = new Map<number, number>();

  constructor(
    public index: Index,
    private postings: Uint8Array,
  ) {
    const n = index.docs.length || 1;
    this.forms = [];
    index.forms.forEach((forms, i) => {
      for (const form of forms) this.forms.push([form, i]);
    });
    this.forms.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    const totals = [0, 0, 0, 0, 0, 0];
    const counts = [n, n, 0, 0, 0, 0];
    for (const doc of index.docs) {
      totals[0] += doc.len[0];
      totals[1] += doc.len[1];
      for (const s of doc.sections) {
        for (let f = 0; f < 4; f++) {
          totals[2 + f] += s.len[f];
          counts[2 + f] += 1;
        }
      }
    }
    this.avgLen = totals.map((t, f) => (counts[f] ? t / counts[f] : 1) || 1);
  }

  private postingsFor(term: number): Posting[] {
    let ps = this.decoded.get(term);
    if (!ps) {
      ps = decodePostings(this.postings, this.index.offsets[term], this.index.offsets[term + 1]);
      this.decoded.set(term, ps);
    }
    return ps;
  }

  private docFreqFor(term: number): number {
    let df = this.docFreq.get(term);
    if (df === undefined) {
      df = new Set(this.postingsFor(term).map((p) => p[0])).size;
      this.docFreq.set(term, df);
    }
    return df;
  }

  /** Index of the first entry whose key is >= prefix. */
  private static lowerBound(prefix: string, length: number, key: (i: number) => string): number {
    let lo = 0;
    let hi = length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (key(mid) < prefix) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  /**
   * Terms matching a query token: the stem exactly at full weight, terms
   * the stem prefixes at reduced weight, and terms whose surface forms the
   * typed word prefixes, so "citatio" reaches "citation" (stem "citat").
   */
  private matchTerms(token: QueryToken): Map<number, number> {
    const { terms } = this.index;
    const out = new Map<number, number>();
    const add = (i: number, weight: number) => out.set(i, Math.max(out.get(i) ?? 0, weight));
    const t = SearchIndex.lowerBound;
    for (let i = t(token.term, terms.length, (j) => terms[j]); i < terms.length && terms[i].startsWith(token.term); i++) {
      add(i, terms[i] === token.term ? 1 : PREFIX_WEIGHT);
    }
    const forms = this.forms;
    for (let i = t(token.form, forms.length, (j) => forms[j][0]); i < forms.length && forms[i][0].startsWith(token.form); i++) {
      add(forms[i][1], forms[i][0] === token.form ? 1 : PREFIX_WEIGHT);
    }
    return out;
  }

  search(query: string): Search {
    const seen = new Set<string>();
    const tokens: QueryToken[] = [];
    for (const run of scan(query)) {
      run.terms.forEach((term, i) => {
        const form = run.forms[i];
        if (seen.has(term)) return;
        seen.add(term);
        tokens.push({ term, form });
      });
    }
    const matched = new Set<string>();
    if (tokens.length === 0) return { results: [], matched };
    const { docs } = this.index;
    const n = docs.length;

    const docScore = new Map<number, number>();
    const sectionScore = new Map<string, number>();
    const blockScore = new Map<string, Map<number, number>>();
    const docTokens = new Map<number, number>();

    for (const token of tokens) {
      // Best contribution per unit for this token, so prefix expansions
      // ("ho" matching how, hope, however) do not stack.
      const unit = new Map<string, number>();
      const block = new Map<string, number>();
      for (const [termIndex, weight] of this.matchTerms(token)) {
        matched.add(this.index.terms[termIndex]);
        const df = this.docFreqFor(termIndex);
        const idf = Math.log(1 + (n - df + 0.5) / (df + 0.5));
        for (const [d, s, b, f, tf] of this.postingsFor(termIndex)) {
          const len = f < 2 ? docs[d].len[f] : docs[d].sections[s].len[f - 2];
          const norm = (tf * (BM25_K1 + 1)) / (tf + BM25_K1 * (1 - BM25_B + (BM25_B * len) / this.avgLen[f]));
          const score = idf * FIELD_WEIGHT[f] * norm * weight;
          const key = f < 2 ? `${d}` : `${d}:${s}`;
          unit.set(key, Math.max(unit.get(key) ?? 0, score));
          if (b >= 0) {
            const bkey = `${d}:${s}:${b}`;
            block.set(bkey, Math.max(block.get(bkey) ?? 0, score));
          }
        }
      }
      const touched = new Set<number>();
      for (const [key, score] of unit) {
        const d = Number(key.split(":")[0]);
        touched.add(d);
        if (key.includes(":")) sectionScore.set(key, (sectionScore.get(key) ?? 0) + score);
        else docScore.set(d, (docScore.get(d) ?? 0) + score);
      }
      for (const [key, score] of block) {
        const at = key.lastIndexOf(":");
        const skey = key.slice(0, at);
        const b = Number(key.slice(at + 1));
        let blocks = blockScore.get(skey);
        if (!blocks) blockScore.set(skey, (blocks = new Map()));
        blocks.set(b, (blocks.get(b) ?? 0) + score);
      }
      for (const d of touched) docTokens.set(d, (docTokens.get(d) ?? 0) + 1);
    }

    const results: Result[] = [];
    for (const [d, matched] of docTokens) {
      if (matched < tokens.length) continue;
      const hits: SectionHit[] = [];
      docs[d].sections.forEach((_section, s) => {
        const score = sectionScore.get(`${d}:${s}`);
        if (!score) return;
        let best = -1;
        let bestScore = 0;
        for (const [b, bs] of blockScore.get(`${d}:${s}`) ?? []) {
          if (bs > bestScore) {
            bestScore = bs;
            best = b;
          }
        }
        hits.push({ section: s, score, block: best });
      });
      hits.sort((a, b) => b.score - a.score);
      const top = hits[0]?.score ?? 0;
      const rest = hits.slice(1).reduce((acc, h) => acc + h.score, 0);
      results.push({ doc: d, score: (docScore.get(d) ?? 0) + top + 0.1 * rest, sections: hits.slice(0, MAX_SECTIONS) });
    }
    results.sort((a, b) => b.score - a.score);
    return { results: results.slice(0, MAX_RESULTS), matched };
  }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Wrap words whose terms the query matched in <mark>, walking text nodes only. */
function highlight(root: HTMLElement, matched: Set<string>): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node as Text);
  for (const node of nodes) {
    const text = node.data;
    const runs = scan(text).filter((r) => r.terms.some((t) => matched.has(t)));
    if (runs.length === 0) continue;
    const fragment = document.createDocumentFragment();
    let last = 0;
    for (const run of runs) {
      if (run.start > last) fragment.append(text.slice(last, run.start));
      fragment.append(el("mark", undefined, text.slice(run.start, run.end)));
      last = run.end;
    }
    if (last < text.length) fragment.append(text.slice(last));
    node.replaceWith(fragment);
  }
}

/** Keep a window of lines around the first match in a code block. */
function trimCode(preview: HTMLElement): void {
  const lines = Array.from(preview.querySelectorAll<HTMLElement>(".line"));
  const window = CODE_CONTEXT_LINES * 2 + 1;
  if (lines.length <= window) return;
  const first = lines.findIndex((l) => l.querySelector("mark"));
  let start = Math.max(0, (first < 0 ? 0 : first) - CODE_CONTEXT_LINES);
  const end = Math.min(lines.length, start + window);
  start = Math.max(0, end - window);
  lines.forEach((line, i) => {
    if (i >= start && i < end) return;
    const next = line.nextSibling;
    if (next && next.nodeType === Node.TEXT_NODE && /^\s*$/.test(next.textContent ?? "")) next.remove();
    line.remove();
  });
  if (start > 0) preview.classList.add("search-preview-trimmed-start");
  if (end < lines.length) preview.classList.add("search-preview-trimmed-end");
}

/** Text length above which a preview is trimmed around its matches. */
const PREVIEW_BUDGET = 500;

function elidedFor(parent: Element): HTMLElement {
  const tag = parent.tagName === "UL" || parent.tagName === "OL" ? "li" : "div";
  return el(tag, "search-elided", "…");
}

/**
 * Trim a long block to the children around its matches, recursively, so a
 * list of paragraphs and code blocks shows only the part that matched.
 * Without matches, keep leading content up to the budget.
 */
function trimLong(root: Element): void {
  if ((root.textContent ?? "").length <= PREVIEW_BUDGET) return;
  const children = Array.from(root.children).filter((c) => !c.classList.contains("search-elided"));
  if (children.length === 0) return;

  const keep = new Set<number>();
  children.forEach((child, i) => {
    if (!child.querySelector("mark")) return;
    keep.add(i - 1);
    keep.add(i);
    keep.add(i + 1);
  });
  if (keep.size === 0) {
    let budget = PREVIEW_BUDGET;
    for (let i = 0; i < children.length && budget > 0; i++) {
      keep.add(i);
      budget -= (children[i].textContent ?? "").length;
    }
  }

  let elided = false;
  children.forEach((child, i) => {
    if (keep.has(i)) {
      if (elided) child.before(elidedFor(root));
      elided = false;
      trimLong(child);
      return;
    }
    // Text between removed children (list separators, newlines) goes too.
    const next = child.nextSibling;
    if (next?.nodeType === Node.TEXT_NODE && /^\s*$/.test(next.textContent ?? "")) next.remove();
    child.remove();
    elided = true;
  });
  if (elided) root.append(elidedFor(root));
}

class SearchUi {
  private index: Promise<SearchIndex> | null = null;
  private stores = new Map<string, Promise<Store>>();
  private styles = new Map<string, Promise<void>>();
  private sequence = 0;
  /** Previews waiting to scroll into view, with the work that fills them. */
  private deferred = new Map<Element, () => void>();
  private observer: IntersectionObserver | null = null;

  constructor(
    private base: URL,
    private input: HTMLInputElement,
    private results: HTMLElement,
  ) {
    if (!results.hasAttribute("aria-live")) results.setAttribute("aria-live", "polite");
    input.addEventListener("focus", () => void this.load());
    input.addEventListener("input", () => void this.run());
    const initial = new URLSearchParams(location.search).get("q");
    if (initial && !input.value) input.value = initial;
    if (input.value || document.activeElement === input) void this.run();
    if (typeof IntersectionObserver !== "undefined") {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const fill = this.deferred.get(entry.target);
            this.deferred.delete(entry.target);
            this.observer?.unobserve(entry.target);
            fill?.();
          }
        },
        { rootMargin: "300px 0px" },
      );
    }
  }

  /** Fill a preview now, or once it is near the viewport when observable. */
  private defer(preview: Element, fill: () => void): void {
    if (!this.observer) {
      fill();
      return;
    }
    this.deferred.set(preview, fill);
    this.observer.observe(preview);
  }

  private load(): Promise<SearchIndex> {
    this.index ??= Promise.all([
      fetch(new URL("index.json", this.base)).then((r) => r.json() as Promise<Index>),
      fetch(new URL("postings.bin", this.base)).then((r) => r.arrayBuffer()),
    ]).then(([index, postings]) => new SearchIndex(index, new Uint8Array(postings)));
    return this.index;
  }

  private store(hash: string): Promise<Store> {
    let p = this.stores.get(hash);
    if (!p) {
      p = fetch(new URL(`docs/${hash}.json`, this.base)).then((r) => r.json());
      this.stores.set(hash, p);
    }
    return p;
  }

  /**
   * Load a page's stylesheets that this page lacks, so its blocks render
   * as they do there. Resolves once they have loaded (or failed), so a
   * preview is not inserted unstyled.
   */
  private ensureStyles(hrefs: string[]): Promise<void> {
    const present = new Set(
      Array.from(document.querySelectorAll<HTMLLinkElement>("link[rel~='stylesheet']")).map((l) => l.href),
    );
    const pending: Promise<void>[] = [];
    for (const href of hrefs) {
      const url = new URL(href, location.href).href;
      if (present.has(url)) continue;
      let p = this.styles.get(url);
      if (!p) {
        p = new Promise<void>((resolve) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = url;
          link.onload = () => resolve();
          link.onerror = () => resolve();
          setTimeout(resolve, 3000);
          document.head.append(link);
        });
        this.styles.set(url, p);
      }
      pending.push(p);
    }
    return Promise.all(pending).then(() => undefined);
  }

  private async run(): Promise<void> {
    const seq = ++this.sequence;
    const query = this.input.value;
    const url = new URL(location.href);
    if (query.trim()) url.searchParams.set("q", query);
    else url.searchParams.delete("q");
    history.replaceState(null, "", url);

    if (!query.trim()) {
      this.results.replaceChildren();
      return;
    }
    const index = await this.load();
    if (seq !== this.sequence) return;

    const { results, matched } = index.search(query);
    if (results.length === 0) {
      this.results.replaceChildren(el("p", "search-empty", `No results for “${query.trim()}”.`));
      return;
    }

    for (const preview of this.deferred.keys()) this.observer?.unobserve(preview);
    this.deferred.clear();

    const list = el("ol", "search-results");
    for (const result of results) {
      const doc = index.index.docs[result.doc];
      const item = el("li", "search-result");
      const title = el("a", "search-result-title", doc.title);
      title.href = doc.url;
      highlight(title, matched);
      item.append(title);
      if (result.sections.length === 0) {
        if (doc.description) {
          const description = el("p", "search-result-description", doc.description);
          highlight(description, matched);
          item.append(description);
        }
      } else {
        const sections = el("ol", "search-result-sections");
        for (const hit of result.sections) {
          const section = doc.sections[hit.section];
          const entry = el("li", "search-result-section");
          const target = section.id ? `${doc.url}#${section.id}` : doc.url;
          const link = el("a", "search-result-crumbs");
          link.href = target;
          if (section.headings.length > 0) {
            for (const heading of section.headings) link.append(el("span", undefined, heading));
            highlight(link, matched);
          } else {
            link.classList.add("search-result-jump");
            link.append(el("span", undefined, "Jump to match"));
          }
          entry.append(link);
          const preview = el("div", "search-preview");
          entry.append(preview);
          sections.append(entry);
          this.defer(preview, () => void this.fillPreview(preview, link, target, doc, hit, matched, seq));
        }
        item.append(sections);
      }
      list.append(item);
    }
    this.results.replaceChildren(list);
  }

  private async fillPreview(
    preview: HTMLElement,
    link: HTMLAnchorElement,
    target: string,
    doc: DocEntry,
    hit: SectionHit,
    matched: Set<string>,
    seq: number,
  ): Promise<void> {
    const store = await this.store(doc.store);
    await this.ensureStyles(store.styles);
    if (seq !== this.sequence) return;
    const blocks = store.blocks[hit.section] ?? [];
    const block = blocks[hit.block] ?? blocks[0];
    if (!block) {
      preview.remove();
      return;
    }
    preview.dataset.kind = block.kind;
    preview.innerHTML = block.html;
    for (const link of Array.from(preview.querySelectorAll<HTMLAnchorElement>("a[href^='#']"))) {
      link.setAttribute("href", doc.url + link.getAttribute("href"));
    }
    // Hidden lines have no toggle in a preview, and a match may sit on one.
    for (const line of Array.from(preview.querySelectorAll<HTMLElement>(".line-hidden"))) {
      line.classList.remove("line-hidden");
      line.dataset.searchHidden = "";
    }
    highlight(preview, matched);
    const directive = textFragment(preview);
    if (directive) link.href = `${target}${target.includes("#") ? "" : "#"}${directive}`;
    if (block.kind === "c") trimCode(preview);
    else trimLong(preview);
  }
}

/** Text the page renders differently from its DOM text, or not at all. */
const UNMATCHABLE_SELECTOR = ".katex, math, svg, [data-search-hidden]";

/**
 * A text fragment directive pointing at the first match in a preview, so
 * the link opens the page scrolled to the matched text. Only the text node
 * the match was split out of supplies prefix and phrase: previews drop
 * some inline elements the page has (sidenote references, hover buttons),
 * so any phrase crossing an element might not exist on the page.
 */
function textFragment(preview: HTMLElement): string | null {
  const mark = preview.querySelector("mark");
  if (!mark || mark.closest(UNMATCHABLE_SELECTOR)) return null;
  const text = (node: ChildNode | null) => (node?.nodeType === Node.TEXT_NODE ? (node.textContent ?? "") : "");
  return fragmentDirective(text(mark.previousSibling), mark.textContent ?? "", text(mark.nextSibling));
}

/** Percent-encode for a text fragment: `-`, `,` and `&` are syntax there. */
function encodeFragmentText(text: string): string {
  return encodeURIComponent(text).replace(/-/g, "%2D");
}

/**
 * Build `:~:text=prefix-,phrase` from the text around a match: up to two
 * words of prefix for disambiguation, the match and up to three following
 * words as the phrase. Original spacing is kept, with whitespace runs
 * collapsed the way the page renders them, so punctuation stays attached.
 */
function fragmentDirective(before: string, match: string, after: string): string | null {
  const collapse = (s: string) => s.replace(/\s+/g, " ").trim();
  if (collapse(match) === "") return null;
  let prefix = collapse(before.match(/(\S+\s*){0,2}$/)?.[0] ?? "");
  // A prefix must sit right before the match, so a trailing punctuation-only
  // word cannot be dropped; drop the whole prefix instead.
  if (prefix !== "" && !/[\p{L}\p{N}]\S*$/u.test(prefix)) prefix = "";
  const phrase = collapse(match + (after.match(/^(\s*\S+){0,3}/)?.[0] ?? ""));
  return `:~:text=${prefix ? `${encodeFragmentText(prefix)}-,` : ""}${encodeFragmentText(phrase)}`;
}

function init(): void {
  const script = document.currentScript as HTMLScriptElement | null;
  const base = new URL(script?.src ?? "/search/search.js", location.href);
  const start = () => {
    const input = document.querySelector<HTMLInputElement>("[data-search-input]");
    const results = document.querySelector<HTMLElement>("[data-search-results]");
    if (input && results) new SearchUi(base, input, results);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
}

if (typeof document !== "undefined") init();

})();
